import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useCommandPaletteContext } from '../CommandPaletteContext';
import { useRegisterCommands } from '../hooks/useRegisterCommands';
import type { Command as CommandType } from '../types';
import { build } from '@/app/routes/build';
import { useTranslation } from 'react-i18next';
import copy from 'copy-to-clipboard';
import { useNotify } from '@/common/notifications';
import { getAccessToken } from '@/features/auth/storage';

/**
 * SystemPlugin - Provides basic global commands for the application
 *
 * This plugin provides commands for:
 * - Copying the access token to the clipboard
 * - Route navigation
 */
export const SystemPlugin: React.FC = () => {
  const { close } = useCommandPaletteContext();
  const history = useHistory();
  const { t } = useTranslation();
  const notify = useNotify();

  // Extract navigation commands from routes
  const routeCommands: CommandType[] = useMemo(() => {
    const commands: CommandType[] = [];

    // Process build routes
    const buildRoutes = build.find(r => Array.isArray(r.path) && r.path.includes('/build'));
    if (buildRoutes?.children) {
      for (const route of buildRoutes.children) {
        const sidebar = route.sidebar;
        if (sidebar && route.path) {
          // Get the first path if it's an array
          const path = Array.isArray(route.path) ? route.path[0] : route.path;
          const groupName = route.group?.name || 'Navigation';

          commands.push({
            id: `navigate-${path}`,
            label: sidebar.translationKey ? t(sidebar.translationKey) : sidebar.name,
            description: `Navigate to ${sidebar.name}`,
            category: groupName,
            keywords: [sidebar.name.toLowerCase(), 'navigate', 'go to'],
            primary: {
              id: `navigate-${path}-action`,
              label: `Go to ${sidebar.name}`,
              action: () => {
                history.push(path);
                close();
              },
            },
          });
        }
      }
    }

    return commands;
  }, [history, t]);

  // Define system commands
  const commands: CommandType[] = useMemo(() => {
    return [
      {
        id: 'copy-access-token',
        label: 'Copy Access Token',
        description: 'Copy your access token to the clipboard',
        category: 'System',
        keywords: ['copy', 'access', 'token', 'clipboard'],
        primary: {
          id: 'copy-access-token-action',
          label: 'Copy Token',
          action: () => {
            const token = getAccessToken();
            if (!token) {
              notify("No access token found in session storage");
              return;
            }

            copy(token);
            notify("Access token coped to clipboard");
            close();
          },
        },
      },
      ...routeCommands, // Add route-based navigation commands
    ];
  }, [routeCommands]);

  // Register commands with the palette
  useRegisterCommands({
    pluginId: 'system-plugin',
    commands,
  });

  // Plugin doesn't render anything
  return null;
};
